/**
 * Calculates totals for a task, including all subtasks and direct materials.
 *
 * @param {Object} params
 * @param {Array} params.material - Array of material items directly on the task.
 * @param {Array} params.subtasks - Array of subtask objects (each with their own materials, labor, etc).
 * @param {boolean} [params.overrideProfitMargin=false] - If true, overrides profit margin for locked sale amounts.
 * @param {string} params.profitMarginType - Type of profit margin: 'PERCENTAGE' or 'FIXED'.
 * @param {number|string} params.profitMargin - The profit margin value to apply if locked.
 * @param {boolean} [params.isForm=false] - If true, expects form-style data structure for labor items.
 *
 * @returns {Object} total - Totals for material, subcontractor, labor, and overall profit, subtotal, forecast, and total.
 *
 * Use case: Used to calculate and summarize all cost, sale, and profit data for a task, including all subtasks and direct materials.
 */

import { Decimal, toDecimal, roundValue } from '../decimal-utils';
const { subTaskCalculation } = require('./calculate-sub-task');
const { isString } = require('lodash');

// Helper function to convert object with numeric values to Decimal values
const toDecimalObject = (obj) => {
  if (!obj) return obj;
  const result = {};
  for (const key in obj) {
    if (obj[key] && typeof obj[key] === 'object' && !(obj[key] instanceof Decimal)) {
      result[key] = toDecimalObject(obj[key]);
    } else {
      result[key] = toDecimal(obj[key] || 0);
    }
  }
  return result;
};

export const taskCalculation = ({
  material = [],
  subtasks = [],
  overrideProfitMargin = false,
  profitMarginType,
  profitMargin,
  isForm = false,
}) => {
  // Initialize totals for each category and overall using Decimal
  let total = {
    subtask: {},
    material: {
      cost: new Decimal(0),
      sale: new Decimal(0),
      profit: new Decimal(0)
    },
    subcontractor: {
      cost: new Decimal(0),
      sale: new Decimal(0),
      profit: new Decimal(0)
    },
    labor: {
      cost: new Decimal(0),
      sale: new Decimal(0),
      profit: new Decimal(0)
    },
    total_profit: new Decimal(0),
    subtotal: new Decimal(0),
    forecast: new Decimal(0),
    forecastHours: new Decimal(0),
    total: new Decimal(0),
  };

  try {
    // Aggregate all subtasks into the task total
    for (const [subIdx, subTask] of subtasks.entries()) {
      // Calculate totals for this subtask
      let subtask_total = subTaskCalculation({
        subtask: subTask,
        profitMargin,
        profitMarginType,
        isForm,
      });

      // Convert subtask totals to Decimal for precise arithmetic
      const subTaskDecimals = {
        material: toDecimalObject(subtask_total.material),
        subcontractor: toDecimalObject(subtask_total.subcontractor),
        labor: toDecimalObject(subtask_total.labor),
        total_profit: toDecimal(subtask_total.total_profit),
        subtotal: toDecimal(subtask_total.subtotal),
        forecast: toDecimal(subtask_total.forecast),
        total: toDecimal(subtask_total.total)
      };

      // Add subtask's material, subcontractor, and labor to the running totals
      total.material.cost = total.material.cost.plus(subTaskDecimals.material.cost);
      total.material.sale = total.material.sale.plus(subTaskDecimals.material.sale);
      total.material.profit = total.material.profit.plus(subTaskDecimals.material.profit);

      total.subcontractor.cost = total.subcontractor.cost.plus(subTaskDecimals.subcontractor.cost);
      total.subcontractor.sale = total.subcontractor.sale.plus(subTaskDecimals.subcontractor.sale);
      total.subcontractor.profit = total.subcontractor.profit.plus(subTaskDecimals.subcontractor.profit);

      total.labor.cost = total.labor.cost.plus(subTaskDecimals.labor.cost);
      total.labor.sale = total.labor.sale.plus(subTaskDecimals.labor.sale);
      total.labor.profit = total.labor.profit.plus(subTaskDecimals.labor.profit);

      // Add subtask's total profit, subtotal, forecast, and total
      total.total_profit = total.total_profit.plus(subTaskDecimals.total_profit);
      total.subtotal = total.subtotal.plus(subTaskDecimals.subtotal);
      total.forecast = total.forecast.plus(subTaskDecimals.forecast);
      total.forecastHours = total.forecastHours.plus(toDecimal(subtask_total.forecastHours || 0));
      total.total = total.total.plus(subTaskDecimals.total);

      // Store subtask totals by subtask id for reference
      total.subtask[subTask.id] = subtask_total;
    }

    // Aggregate direct materials (not in subtasks) into the task total
    let materials = material || [];

    materials.forEach((material, i) => {
      if (!material) return undefined;
      // Parse material if it's a string
      let _material = isString(material) ? JSON.parse(material) : material;

      // Convert material values to Decimal for precise calculations
      const cost = toDecimal(_material['cost'] || 0);
      const qty = toDecimal(_material['qty'] || 1);

      // If sale amount is locked, recalculate using profit margin
      if (_material['saleAmountLock'] === true) {
        const profitMarginDec = toDecimal(profitMargin || 0);

        if (profitMarginType === 'PERCENTAGE') {
          // Calculate sale amount with percentage margin: cost * (1 + profitMargin/100)
          _material['saleAmount'] = roundValue(
            cost.times(profitMarginDec.dividedBy(100).plus(1)),
            2
          );
        } else if (profitMarginType === 'FIXED') {
          // Calculate sale amount with fixed margin: cost + profitMargin
          _material['saleAmount'] = roundValue(
            cost.plus(profitMarginDec),
            2
          );
        }
      }

      // Calculate cost, sale, and profit for this material using Decimal
      const salePrice = toDecimal(_material['saleAmount'] || 0);
      const materialCost = cost.times(qty);
      const materialSale = salePrice.times(qty);
      const materialProfit = materialSale.minus(materialCost);

      // Add to running totals
      total.material.cost = total.material.cost.plus(materialCost);
      total.material.sale = total.material.sale.plus(materialSale);
      total.material.profit = total.material.profit.plus(materialProfit);

      total.total_profit = total.total_profit.plus(materialProfit);
      total.subtotal = total.subtotal.plus(materialCost);
      total.total = total.total.plus(materialSale);
    });

    // Convert all Decimal values back to regular numbers with rounding at the end
    const result = {
      subtask: total.subtask,
      material: {
        cost: roundValue(total.material.cost),
        sale: roundValue(total.material.sale),
        profit: roundValue(total.material.profit)
      },
      subcontractor: {
        cost: roundValue(total.subcontractor.cost),
        sale: roundValue(total.subcontractor.sale),
        profit: roundValue(total.subcontractor.profit)
      },
      labor: {
        cost: roundValue(total.labor.cost),
        sale: roundValue(total.labor.sale),
        profit: roundValue(total.labor.profit)
      },
      total_profit: roundValue(total.total_profit),
      subtotal: roundValue(total.subtotal),
      forecast: roundValue(total.forecast),
      forecastHours: roundValue(total.forecastHours, 2),
      total: roundValue(total.total)
    };

    return result;
  } catch (e) {
    // Log and return partial totals on error
    console.error(e);
    return total;
  }
};
